import React, { useState } from 'react';
import Box from '@cobalt/react-box';
import List, { ClickableItem, Item } from '@cobalt/react-list';
import { Text } from '@cobalt/react-typography';
import Divider from '@cobalt/react-divider';
import Flex from '@cobalt/react-flex';
import Icon from '@cobalt/react-icon';
import { useTheme } from '@cobalt/react-theme-provider';
import { Input as Input$1 } from '@cobalt/react-dropdown';
import Spinner from '@cobalt/react-spinner';
import { UnstyledSearch } from '@cobalt-marketplace/react-search-input';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const Action = ({
  children,
  onClick,
  ...props
}) => {
  return /*#__PURE__*/React.createElement(Box, {
    paddingBottom: "1",
    paddingX: "2"
  }, /*#__PURE__*/React.createElement(ClickableItem, _extends({
    onClick: onClick
  }, props), /*#__PURE__*/React.createElement(Box, {
    padding: "2"
  }, /*#__PURE__*/React.createElement(Text, {
    inline: true
  }, children))));
};

const Actions = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React.createElement(Box, _extends({
    paddingBottom: "2"
  }, props), children, /*#__PURE__*/React.createElement(Box, {
    paddingTop: "1"
  }, /*#__PURE__*/React.createElement(Divider, null)));
};

const DropdownError = ({
  children
}) => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Flex, {
    gap: "1",
    paddingTop: "1"
  }, /*#__PURE__*/React.createElement(Icon, {
    color: theme.red700,
    name: "error_outline",
    size: "micro"
  }), /*#__PURE__*/React.createElement(Text, {
    color: theme.red700,
    size: "small"
  }, children));
};

const Option = ({
  children,
  description,
  disabled = false,
  style,
  title
}) => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Flex, {
    padding: "2"
  }, children, /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "2",
    style: {
      overflow: "hidden",
      ...style,
      opacity: disabled ? 0.5 : 1
    }
  }, /*#__PURE__*/React.createElement(Text, {
    truncated: true
  }, title), description && /*#__PURE__*/React.createElement(Text, {
    color: theme.gray600,
    size: "small"
  }, description)));
};

const GroupOptions = ({
  borderBottom = false,
  children,
  title,
  ...props
}) => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Item, props, /*#__PURE__*/React.createElement(Box, {
    paddingBottom: "1",
    paddingTop: "2",
    paddingX: "4"
  }, /*#__PURE__*/React.createElement(Text, {
    inline: true,
    weight: "medium",
    color: theme.gray600,
    style: {
      textTransform: "uppercase"
    }
  }, title)), /*#__PURE__*/React.createElement(List, null, children), borderBottom && /*#__PURE__*/React.createElement(Box, {
    paddingTop: "1"
  }, /*#__PURE__*/React.createElement(Divider, null)));
};

const Input = ({
  children,
  disabled,
  dismissible,
  error,
  loading,
  onClear,
  onClick,
  open,
  placeholder,
  ...props
}) => {
  const theme = useTheme();
  const selected = !!children;
  const arrowAnimation = {
    transform: `rotate(${open ? 180 : 0}deg)`,
    transition: "transform .2s ease"
  };
  return /*#__PURE__*/React.createElement(Input$1, _extends({
    disabled: disabled,
    onClick: event => !(disabled || loading) && onClick(event),
    variation: error ? "error" : undefined
  }, props), /*#__PURE__*/React.createElement(Flex, {
    alignX: "space-between",
    alignY: "center",
    height: "100%",
    paddingLeft: "1"
  }, /*#__PURE__*/React.createElement(Text, {
    inline: true,
    truncated: true,
    color: !selected || loading ? theme.gray500 : undefined
  }, selected ? children : placeholder), /*#__PURE__*/React.createElement(Flex, {
    alignY: "center"
  }, selected && dismissible && /*#__PURE__*/React.createElement(Icon, {
    color: theme.gray600,
    name: "close",
    onClick: onClear,
    size: "tiny"
  }), loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: "tiny",
    style: {
      height: 20
    }
  }) : /*#__PURE__*/React.createElement(Icon, {
    style: arrowAnimation,
    name: "expand_more",
    size: "small"
  }))));
};

const defaultMessages = {
  error: "An error occurred while trying to load more results",
  loading: "Loading...",
  loadMore: "Load more...",
  tryAgain: "Try again..."
};

const LoadMore = ({
  messages,
  onLoad,
  visible,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const theme = useTheme();

  if (!visible) {
    return null;
  } // Avoid having elements without translations


  const translations = { ...defaultMessages,
    ...messages
  };

  const done = () => {
    setLoading(false);
  };

  const failed = () => {
    setHasError(true);
    done();
  };

  if (loading) {
    return /*#__PURE__*/React.createElement(Box, _extends({
      paddingTop: "1"
    }, props), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Flex, {
      alignX: "center",
      alignY: "center",
      gap: "2",
      paddingBottom: "2",
      paddingTop: "4"
    }, /*#__PURE__*/React.createElement(Spinner, {
      size: "small"
    }), /*#__PURE__*/React.createElement(Text, {
      inline: true
    }, translations.loading)));
  }

  return /*#__PURE__*/React.createElement(Box, {
    paddingTop: "1"
  }, /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Box, {
    paddingTop: "2",
    paddingX: "2"
  }, /*#__PURE__*/React.createElement(ClickableItem, _extends({}, props, {
    // This needs to come after the spread because of the onClick that comes from the dropdown
    onClick: event => {
      event.stopPropagation();
      setLoading(true);
      setHasError(false);
      onLoad(done, failed);
    }
  }), /*#__PURE__*/React.createElement(Box, {
    padding: "2"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignX: "center",
    direction: "column"
  }, hasError && /*#__PURE__*/React.createElement(Flex, {
    alignX: "center",
    gap: "1",
    paddingBottom: "2"
  }, /*#__PURE__*/React.createElement(Icon, {
    color: theme.red700,
    name: "error_outline",
    size: "micro"
  }), /*#__PURE__*/React.createElement(Text, {
    color: theme.red700,
    size: "small"
  }, translations.error)), /*#__PURE__*/React.createElement(Text, {
    color: theme.primary600,
    weight: "medium"
  }, hasError ? translations.tryAgain : translations.loadMore))))));
};

const OptionsWrapper = ({
  children,
  role
}) => /*#__PURE__*/React.createElement(Box, {
  scrollable: true,
  paddingY: "2",
  style: {
    maxHeight: 250
  },
  role: role
}, /*#__PURE__*/React.createElement(List, null, children));

const searchInChildren = (query, children) => {
  return React.Children.toArray(children).filter(child => {
    const title = child.props.title;

    if (!title) {
      return false;
    }

    return title.toLowerCase().includes(query.toLowerCase());
  });
};

const defaultMessages$1 = {
  noResults: "No results found",
  placeholder: "Search"
};

const Search = ({
  children,
  messages,
  onChangeSearch,
  onClick,
  onSearch = searchInChildren,
  ...props
}) => {
  const theme = useTheme();
  const [query, setQuery] = useState(""); // Avoid having elements without translations

  const translations = { ...defaultMessages$1,
    ...messages
  };
  let results = query && onSearch && onSearch(query, children) || children;

  if (typeof children === "function") {
    results = children(query);
  }

  const onChange = query => {
    setQuery(query);
    onChangeSearch === null || onChangeSearch === void 0 ? void 0 : onChangeSearch(query);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    height: "40px",
    paddingX: "2"
  }, /*#__PURE__*/React.createElement(UnstyledSearch, _extends({
    dismissable: true,
    onSearch: onChange,
    placeholder: translations.placeholder
  }, props))), /*#__PURE__*/React.createElement(Divider, null), React.Children.toArray(results).length > 0 ? /*#__PURE__*/React.createElement(OptionsWrapper, {
    role: "status"
  }, React.Children.map(results, result => {
    if ( /*#__PURE__*/React.isValidElement(result)) {
      var _result$props$onClick;

      return /*#__PURE__*/React.cloneElement(result, {
        // Try to use first the component onClick. If does not exist, use the
        // one by default
        onClick: (_result$props$onClick = result.props.onClick) !== null && _result$props$onClick !== void 0 ? _result$props$onClick : onClick
      });
    }

    return result;
  })) : /*#__PURE__*/React.createElement(Box, {
    padding: "4",
    role: "status"
  }, /*#__PURE__*/React.createElement(Text, {
    color: theme.gray500
  }, translations.noResults)));
};

export { Action, Actions, DropdownError, Option as GenericOption, GroupOptions, Input, LoadMore, OptionsWrapper, Search };
